/**
 * command-palette.tsx — ⌘K command surface. Lists every CQRS contract grouped by
 * pipeline phase, generates a form from each contract's `inputSchema`, shows the
 * risk badge, and runs the high-risk approval flow (the agent-harness pattern).
 * Keyboard-first: ↑↓ navigate, Tab dives into the form, Esc closes.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { listContracts, runCommand, runQuery, getState, onDataChanged, type ContractMeta, type CommandResult } from "../lib/api.ts";
import type { JsonSchema } from "../lib/api.ts";
import type { QueryResult } from "../../cqrs/contract.ts";
import type { Workspace } from "../../store/events.ts";
import { listRefs } from "../../domain/resolve.ts";
import { SchemaForm } from "../lib/schema-form.tsx";
import { navigate } from "../lib/router.tsx";
import { useT } from "../lib/i18n.tsx";
import { keepInView } from "../lib/scroll.ts";

const GROUP_ORDER = ["Elicit", "Capture", "Synthesize", "Author", "Evaluate", "Package", "Project", "Inspect"];

/** Index of the first item of the group AFTER the one at `h` (clamps to the last item). */
function nextGroupStart(list: readonly ContractMeta[], h: number): number {
  const g = list[h]?.group;
  for (let i = h + 1; i < list.length; i++) if (list[i]!.group !== g) return i;
  return Math.max(0, list.length - 1);
}
/** Index of the first item of the current group at `h`. */
function groupStart(list: readonly ContractMeta[], h: number): number {
  const g = list[h]?.group;
  let start = h;
  while (start > 0 && list[start - 1]!.group === g) start--;
  return start;
}
/**
 * The "jump up by group" target — the inverse of {@link nextGroupStart}. From
 * mid-group it snaps to the current group's first item; when already at that
 * first item it jumps to the PREVIOUS group's first item (so ⌥↓ then ⌥↑ returns
 * you across the same boundary). Clamps to the first item.
 */
function prevGroupStart(list: readonly ContractMeta[], h: number): number {
  const cur = groupStart(list, h);
  if (h > cur) return cur; // mid-group → snap to this group's header
  return cur > 0 ? groupStart(list, cur - 1) : 0; // at header → previous group's header
}

export function useCommandPalette(): { open: boolean; setOpen: (v: boolean) => void } {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return { open, setOpen };
}

interface Pending {
  contract: ContractMeta;
  input: unknown;
  hint: string;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element | null {
  const [contracts, setContracts] = useState<readonly ContractMeta[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [filter, setFilter] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CommandResult | null>(null);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  // The form has unsubmitted edits since the last run — Run stays enabled; once a
  // result reflects the current input it goes false, disabling (and tab-skipping) Run.
  const [dirty, setDirty] = useState(true);
  const [resultHighlight, setResultHighlight] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);
  const [ws, setWs] = useState<Workspace | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const t = useT();

  // Load the command surface. Tracked as a status so a failed/slow fetch is visible
  // and recoverable — never silently swallowed into a dead "No matches" box.
  const load = useCallback(() => {
    setStatus("loading");
    listContracts()
      .then((cs) => {
        setContracts(cs);
        setStatus(cs.length > 0 ? "ready" : "error");
      })
      .catch(() => setStatus("error"));
  }, []);

  // (Re)load whenever the palette opens, so it always reflects the live registry.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Load the workspace so reference fields can render as name-pickers, and keep it
  // fresh after any command writes (a new entity should be pickable immediately).
  useEffect(() => {
    if (!open) return;
    const refresh = (): void => {
      getState()
        .then(setWs)
        .catch(() => undefined);
    };
    refresh();
    return onDataChanged(refresh);
  }, [open]);

  const lookupRefs = useCallback((kind: string) => (ws ? listRefs(ws, kind) : []), [ws]);

  // Self-heal: while open and failing (e.g. the dev server is mid hot-reload), keep
  // retrying so the palette fills in as soon as the API comes back.
  useEffect(() => {
    if (!open || status !== "error") return;
    const id = setTimeout(load, 1500);
    return () => clearTimeout(id);
  }, [open, status, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    // Token AND-match (not one contiguous substring), so "capture evidence" finds
    // "Capture an evidence item" and "query" narrows to queries by kind.
    const tokens = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = tokens.length
      ? contracts.filter((c) => {
          const hay = `${c.name} ${c.title} ${c.description} ${c.group} ${c.kind}`.toLowerCase();
          return tokens.every((tok) => hay.includes(tok));
        })
      : contracts;
    return [...list].sort((a, b) => {
      const ga = GROUP_ORDER.indexOf(a.group);
      const gb = GROUP_ORDER.indexOf(b.group);
      return ga !== gb ? ga - gb : 0;
    });
  }, [contracts, filter]);

  // The active contract is DERIVED from the highlight, not stored via a passive effect.
  // This is what keeps the row's active marker from lagging: `.highlight` (driven by
  // `highlight`) and `.selected` (driven by `selected`) BOTH style the active row, so if
  // `selected` were set a render later (the old passive-effect approach) the previous row
  // would keep `.selected` — i.e. stay looking active — for one extra paint. Deriving
  // `selected` makes both classes land on the same row in the same render.
  const selected = filtered[Math.min(highlight, filtered.length - 1)] ?? null;

  useEffect(() => setHighlight(0), [filter]);
  // When the active contract changes, clear its transient panels. Keyed on the derived
  // selection's name, so it fires exactly when selection changes — not every keypress.
  useEffect(() => {
    setResult(null);
    setQueryResult(null);
    setPending(null);
    setDirty(true);
  }, [selected?.name]);

  // Keep the highlighted command row in view — the SAME pattern as the NamePicker
  // dropdown and the query list: useLayoutEffect keyed on the highlight, so the row's
  // `.highlight` class is committed to the DOM before we scroll (select → then scroll,
  // one frame, before paint), and keepInView writes only this list's scrollTop.
  useLayoutEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector<HTMLElement>(".palette-item.highlight");
    if (list && el) keepInView(list, el);
  }, [highlight, filtered]);

  // The query-result list scrolls EXACTLY like the NamePicker dropdown (the proven,
  // lag-free reference). Two things make that work, both mirrored here:
  //   1. The list (.query-rows) is its OWN bounded scroll container (see CSS:
  //      max-height + overflow-y) — so keepInView writes only this small box's
  //      scrollTop by the exact overflow. (scrollIntoView walked up to .palette-detail
  //      and scrolled the whole pane, which is the "scrolls too much" over-scroll.)
  //   2. The scroll runs in a useLayoutEffect keyed on the highlight, so the row's
  //      `.hi` class is already committed to the DOM before we scroll — select → then
  //      scroll, in the same frame, before paint.
  // focus({ preventScroll }) keeps the list keyboard-live without the browser's own
  // focus-scroll fighting keepInView.
  useLayoutEffect(() => {
    if (!queryResult || queryResult.rows.length === 0) return;
    const box = resultsRef.current;
    const el = box?.querySelectorAll<HTMLElement>(".query-row")[Math.min(resultHighlight, queryResult.rows.length - 1)];
    if (box && el) {
      el.focus({ preventScroll: true });
      keepInView(box, el);
    }
  }, [queryResult, resultHighlight]);

  if (!open) return null;

  const submit = async (input: unknown, approvalGranted = false): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    setResult(null);
    setQueryResult(null);
    // Queries READ — they go to /api/queries, return rows, and never mutate or
    // navigate away. Commands WRITE through the approval-gated command path.
    if (selected.kind === "query") {
      try {
        const qr = await runQuery<QueryResult>(selected.name, input);
        setQueryResult(qr);
        setResultHighlight(0);
        setDirty(false); // the visible result now reflects the current input
      } catch (err) {
        setResult({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      } finally {
        setBusy(false);
      }
      return;
    }
    const r = await runCommand(selected.name, input, approvalGranted);
    setBusy(false);
    if (r.kind === "approval_required") {
      setPending({ contract: selected, input, hint: r.hint });
      return;
    }
    setResult(r);
    if (r.kind === "ok" && r.value.goto) {
      const goto = r.value.goto;
      setTimeout(() => {
        onClose();
        navigate(goto.startsWith("#") ? goto.slice(1) : goto);
      }, 500);
    }
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // ⇧↓ / ⇧↑ is the browser's native "extend text selection"; never hijack it
    // for list navigation, or the search box can't be selected with the keyboard.
    if (e.key === "ArrowDown" && !e.shiftKey) {
      // ⌥↓ jumps to the start of the next group; ↓ steps one. The scroll follows in
      // the useLayoutEffect keyed on `highlight` (select → then scroll).
      e.preventDefault();
      setHighlight((h) => (e.altKey ? nextGroupStart(filtered, h) : Math.min(filtered.length - 1, h + 1)));
    } else if (e.key === "ArrowUp" && !e.shiftKey) {
      // ⌥↑ jumps to the start of the previous group (inverse of ⌥↓); ↑ steps one.
      e.preventDefault();
      setHighlight((h) => (e.altKey ? prevGroupStart(filtered, h) : Math.max(0, h - 1)));
    } else if (e.key === "Tab" && !e.shiftKey) {
      const target = detailRef.current?.querySelector<HTMLElement>("input, textarea, select, button");
      if (target) {
        e.preventDefault();
        target.focus();
      }
    }
  };

  // Shift+Tab inside the form behaves normally (moves to the previous field). Only
  // when focus is on the FIRST focusable field — with nowhere left to go up — does it
  // escape back to the search box, where the arrow keys drive the list again.
  const onDetailKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== "Tab" || !e.shiftKey) return;
    const focusables = detailRef.current?.querySelectorAll<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables && focusables.length > 0 ? focusables[0] : null;
    if (first && document.activeElement === first) {
      e.preventDefault();
      searchRef.current?.focus();
    }
  };

  let lastGroup = "";

  return (
    <div className="palette-overlay" onClick={onClose}>
      <div className="palette" role="dialog" aria-label="Command palette" onClick={(e) => e.stopPropagation()}>
        <div className="palette-header">
          <span className="gly" style={{ color: "var(--faint)" }}>
            ⌕
          </span>
          <input
            ref={searchRef}
            autoFocus
            className="palette-search"
            placeholder={t("palette.search")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={onSearchKey}
          />
          <button className="btn ghost sm" tabIndex={-1} onClick={onClose}>
            esc
          </button>
        </div>
        <div className="palette-body">
          <div
            className="palette-list"
            ref={listRef}
            role="listbox"
            aria-label="Contracts"
            aria-activedescendant={filtered[highlight] ? `palette-item-${filtered[highlight]!.name}` : undefined}
          >
            {filtered.map((c, i) => {
              const header = c.group !== lastGroup;
              lastGroup = c.group;
              return (
                <div key={c.name}>
                  {header ? <div className="palette-group">{c.group}</div> : null}
                  <button
                    id={`palette-item-${c.name}`}
                    role="option"
                    aria-selected={selected?.name === c.name}
                    // No onMouseEnter: hover is a purely visual state (CSS `.palette-item:hover`).
                    // Activating a row — moving the highlight and swapping the detail panel — is a
                    // deliberate act (click or arrow keys), never an accident of the cursor passing
                    // over the list. Driving `highlight` from hover made every crossed row "active",
                    // silently swapping the detail and discarding any in-progress form/result.
                    className={`palette-item ${i === highlight ? "highlight" : ""} ${selected?.name === c.name ? "selected" : ""}`}
                    onClick={() => {
                      setHighlight(i);
                      searchRef.current?.focus();
                    }}
                  >
                    <div className="palette-item-row">
                      <span className="palette-name">{c.title}</span>
                      <span className="palette-spacer" />
                      {c.kind === "query" ? <span className="badge query">query</span> : null}
                      <span className={`badge risk-${c.approval.defaultRiskLevel}`}>{c.approval.defaultRiskLevel}</span>
                    </div>
                    <div className="palette-item-desc">{c.description}</div>
                  </button>
                </div>
              );
            })}
            {filtered.length === 0 ? (
              status === "loading" || status === "idle" ? (
                <div className="palette-group">{t("palette.loading")}</div>
              ) : status === "error" ? (
                <div className="palette-empty" style={{ padding: "16px 18px" }}>
                  <div style={{ marginBottom: 10 }}>{t("palette.error")}</div>
                  <button className="btn ghost sm" onClick={load}>{t("palette.retry")}</button>
                </div>
              ) : contracts.length === 0 ? (
                <div className="palette-empty" style={{ padding: "16px 18px" }}>
                  <div style={{ marginBottom: 10 }}>{t("palette.error")}</div>
                  <button className="btn ghost sm" onClick={load}>{t("palette.retry")}</button>
                </div>
              ) : (
                <div className="palette-group">{t("palette.noMatches")}</div>
              )
            ) : null}
          </div>
          <div className="palette-detail" ref={detailRef} onKeyDown={onDetailKey}>
            {selected ? (
              <>
                <h3>{selected.title}</h3>
                <code className="command-name">{selected.name}</code>
                <p className="command-desc">{selected.description}</p>
                <div className="command-meta">
                  <span className={`badge ${selected.kind}`}>{selected.kind}</span>
                  <span className={`badge risk-${selected.approval.defaultRiskLevel}`}>
                    risk: {selected.approval.defaultRiskLevel}
                  </span>
                  {selected.effects.writes.length > 0 ? (
                    <span className="badge">writes: {selected.effects.writes.join(", ")}</span>
                  ) : null}
                </div>
                <SchemaForm
                  key={selected.name}
                  schema={selected.inputSchema as JsonSchema}
                  submitLabel={selected.kind === "command" ? t("common.execute") : t("common.run")}
                  busy={busy}
                  // A query whose result already reflects the current input needn't re-run:
                  // Run is disabled (and thus skipped by Shift+Tab, jumping back to the form).
                  submitDisabled={selected.kind === "query" && !dirty && queryResult !== null}
                  onChange={() => setDirty(true)}
                  onSubmit={(input) => submit(input, false)}
                  lookupRefs={lookupRefs}
                />
                {pending ? (
                  <div className="approval-dialog">
                    <div className="approval-title">{t("palette.approval")}</div>
                    <p style={{ fontSize: 13, color: "var(--paper-dim)", marginBottom: 10 }}>{pending.hint}</p>
                    <div className="row">
                      <button className="btn ghost sm" onClick={() => setPending(null)}>
                        {t("common.cancel")}
                      </button>
                      <button
                        className="btn warn sm"
                        onClick={() => {
                          const input = pending.input;
                          setPending(null);
                          void submit(input, true);
                        }}
                      >
                        {t("palette.approve")}
                      </button>
                    </div>
                  </div>
                ) : null}
                {result && result.kind === "ok" ? (
                  <div className="result ok">✓ {result.value.message ?? "Done"}</div>
                ) : null}
                {result && result.kind === "error" ? <div className="result error">{result.message}</div> : null}
                {queryResult ? (
                  <div className="query-result">
                    <div className="query-summary">{queryResult.summary}</div>
                    {queryResult.rows.length > 0 ? (
                      <div
                        className="query-rows"
                        ref={resultsRef}
                        role="listbox"
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setResultHighlight((h) => Math.min(queryResult.rows.length - 1, h + 1));
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setResultHighlight((h) => Math.max(0, h - 1));
                          }
                        }}
                      >
                        {queryResult.rows.map((row, i) => {
                          const go = row.route;
                          return (
                            <button
                              key={`${row.id ?? row.label}-${i}`}
                              className={`query-row ${go ? "nav" : ""} ${i === resultHighlight ? "hi" : ""}`}
                              role="option"
                              aria-selected={i === resultHighlight}
                              tabIndex={i === resultHighlight ? 0 : -1}
                              onFocus={() => setResultHighlight(i)}
                              onClick={() => {
                                if (!go) return;
                                onClose();
                                navigate(go.startsWith("#") ? go.slice(1) : go);
                              }}
                            >
                              <div className="query-row-head">
                                <span className="query-row-label">{row.label}</span>
                                {row.sublabel ? <span className="query-row-sub">{row.sublabel}</span> : null}
                                {go ? <span className="query-row-go" aria-hidden="true">→</span> : null}
                              </div>
                              {row.detail ? <div className="query-row-detail">{row.detail}</div> : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="query-empty">{t("palette.queryEmpty")}</div>
                    )}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="palette-empty">{t("palette.empty")}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
